import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { feeSchedule, fmtCents, moneyRange } from '../utils/fees';

const total =
  "grid gap-1.5 px-8 py-6 [flex:1_1_0] [grid-template-areas:'label'_'value'_'note'] [&+&]:border-l [&+&]:border-l-[--q-border] max-[767px]:flex max-[767px]:items-baseline max-[767px]:justify-between max-[767px]:gap-4 max-[767px]:px-0 max-[767px]:py-4 max-[767px]:[&+&]:border-t max-[767px]:[&+&]:border-t-[--q-border] max-[767px]:[&+&]:[border-left:0]";
const totalText = 'contents max-[767px]:flex max-[767px]:flex-col max-[767px]:gap-0.5';
const totalNote = 'text-sm leading-5 text-[--q-muted] [grid-area:note] [&_p]:m-0 max-[767px]:text-[13px] max-[767px]:leading-[18px]';
const totalValue =
  'font-heading text-[36px] font-light leading-[42px] tracking-[--q-tracking-tight] [grid-area:value] max-[767px]:text-[28px] max-[767px]:font-normal max-[767px]:leading-8 max-[767px]:tracking-[0]';

/** The two numbers people come for: known move-in charges and the required monthly essentials. */
export function FeeTotals() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const s = feeSchedule(p);
  const monthly = moneyRange(s.monthlyLo, s.monthlyHi);

  if (s.moveInTotal == null && !monthly && !editing) return null;

  return (
    <section className="px-[--gutter] pb-2 max-[767px]:pb-3 max-[767px]:pt-2">
      <div className="flex rounded-lg bg-[--q-surface] max-[767px]:flex-col max-[767px]:px-5 max-[767px]:py-1" data-reveal>
        {(s.moveInTotal != null || editing) && (
          <div className={total}>
            <div className={totalText}>
              <RichTextContent path="feetotals.movein_label" as="div" className="eyebrow [grid-area:label]" />
              <RichTextContent path="feetotals.movein_note" as="div" className={totalNote} />
            </div>
            <div className={totalValue}>{s.moveInTotal != null ? fmtCents(s.moveInTotal) : '—'}</div>
          </div>
        )}
        {(monthly || editing) && (
          <div className={total}>
            <div className={totalText}>
              <RichTextContent path="feetotals.monthly_label" as="div" className="eyebrow [grid-area:label]" />
              <RichTextContent path="feetotals.monthly_note" as="div" className={totalNote} />
            </div>
            <div className={totalValue}>{monthly || '—'}</div>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTotals',
  displayName: 'Fee Totals',
  tokens: {
    'feetotals.movein_label': { label: 'Move-in Label', type: 'richtext', defaultValue: '<p>Move-in</p>' },
    'feetotals.movein_note': { label: 'Move-in Note', type: 'richtext', defaultValue: '<p>Known charges for one adult, before the deposit.</p>' },
    'feetotals.monthly_label': { label: 'Monthly Label', type: 'richtext', defaultValue: '<p>Monthly essentials</p>' },
    'feetotals.monthly_note': { label: 'Monthly Note', type: 'richtext', defaultValue: '<p>Required every month, before rent.</p>' },
  },
};

(FeeTotals as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeTotals;
